import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

/**
 * Email addresses and telephone numbers as actions. An address opens the user's own mail program
 * (Outlook, Gmail…) with a new message, so what they send is in their own Sent folder; a number
 * can be dialled, and opens a WhatsApp chat. IBMS sends nothing itself: sending from inside IBMS
 * is the Microsoft 365 / Outlook integration of a later phase (FRD-INT-038).
 *
 * A masked value (SDD §10.3) is not a value: it is shown as masked text, never as a link.
 */
const MASK = '••••';
const isMasked = (value: string) => value.startsWith(MASK);

/** A plausible single address: one "@" with text either side and no spaces. */
export function emailOf(value: string | null | undefined): string | null {
  const v = value?.trim() ?? '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
}

/**
 * The international digits WhatsApp's click-to-chat needs (wa.me/<digits>): "+" or "00" starts an
 * international number; a number starting with a single 0 is a UAE number, TAQON's only location
 * (D6), so 0 is replaced by 971. Anything shorter than 8 digits is not offered.
 */
export function whatsAppDigits(value: string | null | undefined): string | null {
  const v = value?.trim() ?? '';
  if (v === '' || isMasked(v)) return null;
  let digits = v.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  else if (digits.startsWith('00')) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = `971${digits.slice(1)}`;
  digits = digits.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15 ? digits : null;
}

export function EmailLink({
  email,
  subject,
}: {
  email: string | null | undefined;
  /** Optional subject for the new message, for example the record it is about. */
  subject?: string;
}) {
  if (email === null || email === undefined || email.trim() === '') return <>—</>;
  if (isMasked(email)) return <>{email} (masked)</>;
  const address = emailOf(email);
  if (address === null) return <>{email}</>;
  const href = `mailto:${address}${subject === undefined ? '' : `?subject=${encodeURIComponent(subject)}`}`;
  return (
    <Link
      href={href}
      aria-label={`Email ${address}`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, overflowWrap: 'anywhere' }}
    >
      <MailOutlineIcon fontSize="inherit" aria-hidden />
      {address}
    </Link>
  );
}

export function PhoneLinks({ phone }: { phone: string | null | undefined }) {
  if (phone === null || phone === undefined || phone.trim() === '') return <>—</>;
  if (isMasked(phone)) return <>{phone} (masked)</>;
  const shown = phone.trim();
  const wa = whatsAppDigits(shown);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Link href={`tel:${shown.replace(/[^\d+]/g, '')}`} aria-label={`Call ${shown}`}>
        {shown}
      </Link>
      {wa !== null && (
        <Tooltip title="Chat on WhatsApp">
          <IconButton
            size="small"
            component="a"
            href={`https://wa.me/${wa}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`WhatsApp ${shown}`}
            sx={{ color: '#128C4B', p: 0.5 }}
          >
            <WhatsAppIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
